import React from 'react';

import { isFunction, sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Button, Tag, Flex } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';

import Weight from 'components/controls/Weight';
import { getScoreColor } from 'utils/ranking';
import { getItemAlteredByScenario } from 'utils/rules';
import {
	FishboneLinkTarget,
	TARGET_CONFIG,
	useFishboneLinkController
} from 'hooks/useFishboneLinkController';

interface FishboneLinkSectionProps {
	// Cible du lien (risk/stake/indicator/stakeholder)
	target: FishboneLinkTarget;
	// La branche ou sous-arete selectionnee, porteuse de selection[`${target}Links`]
	selection: any;
	nodeType: 'branch' | 'item';
	// Objets liables (deja l'ensemble du projet, filtres ici sur enabled)
	items: any[];
	scenario: any;
	// Titre de section
	title: string;
	// Repli
	visible: boolean;
	onToggleVisible?: () => void;
	// Met a jour l'etat local de selection apres mutation
	select?: Function;
	// Filtre additionnel (ex: isOpportunity true/false pour separer risques/ouvertures)
	filter?: (item: any) => boolean;
	// Affiche la pastille de tendance (faux pour stakeholders)
	showTrend?: boolean;
	disabled?: boolean;
}

export const FishboneLinkSection = (props: FishboneLinkSectionProps) => {
	const { t } = useTranslation();
	const { createLink, deleteLink, updateLinkStrength } =
		useFishboneLinkController();

	const cfg = TARGET_CONFIG[props.target];
	const field = cfg.field;
	const targetIdName = cfg.targetIdName;

	const links: any[] = props.selection?.[field] || [];
	const linkByTargetId = (targetId: string) =>
		links.find((l: any) => l && l[targetIdName] === targetId);

	const filtered = (props.items || [])
		.map((i: any) => getItemAlteredByScenario(i, props.scenario))
		.filter((i: any) => i.enabled !== false)
		.filter((i: any) => (props.filter ? props.filter(i) : true));

	// Met a jour selection[field] localement (optimiste cote composant parent)
	const setLinks = (next: any[]) => {
		if (isFunction(props.select))
			props.select({ ...props.selection, [field]: next });
	};

	const onToggle = async (item: any) => {
		if (props.disabled) return;
		const link = linkByTargetId(item.id);
		if (!link || !link.id) {
			const created = await createLink(
				props.target,
				props.nodeType,
				props.selection?.id,
				item.id
			);
			if (created) setLinks([...links, created]);
		} else if (link.strength < 3) {
			await updateLinkStrength(props.target, link, link.strength + 1);
			setLinks(
				links.map((l: any) =>
					l.id === link.id ? { ...l, strength: l.strength + 1 } : l
				)
			);
		} else {
			await deleteLink(
				props.target,
				link.id,
				props.nodeType,
				props.selection?.id,
				item.id
			);
			setLinks(links.filter((l: any) => l.id !== link.id));
		}
	};

	return (
		<Flex vertical className="mt-2">
			<Flex className="text-left mb-05">
				{props.title}
				{isFunction(props.onToggleVisible) && (
					<Button
						shape="circle"
						className="mini ml-auto"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							props.onToggleVisible!();
						}}
					>
						<FontAwesomeIcon
							icon={props.visible ? faChevronUp : faChevronDown}
						/>
					</Button>
				)}
			</Flex>

			{props.visible && (
				<div className="text-left">
					{filtered && filtered.length ? (
						sortBy(filtered, 'name').map((i: any) => {
							const link = linkByTargetId(i.id);
							return (
								<Tag.CheckableTag
									key={i.id}
									checked={!!link}
									onClick={() => onToggle(i)}
									className="ImpactedRisk tag"
								>
									{props.showTrend !== false && (
										<div
											className={'Trend v' + i.trend}
											style={
												i.trend == '50'
													? {}
													: {
															background:
																getScoreColor(
																	i.trend
																)
													  }
											}
										></div>
									)}
									<span className="tag__label" title={i.name}>
										{i.name}
									</span>
									{link ? (
										<Weight onClick={() => {}}>
											{link.strength}
										</Weight>
									) : null}
								</Tag.CheckableTag>
							);
						})
					) : (
						<div
							className="text-center"
							style={{ color: 'var(--ant-color-text-tertiary)' }}
						>
							{t('messages.not_defined')}
						</div>
					)}
				</div>
			)}
		</Flex>
	);
};

export default FishboneLinkSection;
